import { createSlice } from '@reduxjs/toolkit';

import {
  AccountType,
  Address,
  AddressGroup,
  Phone,
  PhoneGroup,
  UpdateAccountInfoInput,
  UpdateAccountInfoSmbInput,
} from '@mfe/shared/schema-types';

import { ErrorsObj, RuntimeError } from '../graphql';

type ErrorPayload = RuntimeError | ErrorsObj | undefined | null;

type UserInfoState = {
  loading: boolean;
  wasUpdateSuccessful: boolean;
  error: ErrorPayload;
  userInfo: {
    username: string;
    partyId: string;
    accountNumber: string;
    address: AddressGroup;
    isPreInstall: boolean;
    accountType: AccountType;
    businessName: string;
    fullName: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: PhoneGroup;
    accountHolder: IndividualInfo;
  };
};

type IndividualInfo = {
  fullName: string;
  firstName: string;
  lastName: string;
  partyId: string;
  email: string;
  phone: PhoneGroup;
};

const blankAddress: Address = {
  addressLines: [],
  region: '',
  postalCode: '',
  municipality: '',
  countryCode: '',
};

const blankPhone: Phone = {
  phoneNumber: '',
  labels: [],
};

const initialState: UserInfoState = {
  loading: true,
  wasUpdateSuccessful: false,
  error: null,
  userInfo: {
    businessName: '',
    fullName: '',
    firstName: '',
    lastName: '',
    username: '',
    partyId: '',
    accountNumber: '',
    email: '',
    phone: {
      primary: blankPhone,
      mobile: null,
      secondary: blankPhone,
    },
    address: {
      billing: blankAddress,
      service: blankAddress,
      shipping: blankAddress,
    },
    isPreInstall: false,
    accountType: AccountType.Residential,
    accountHolder: {
      fullName: '',
      firstName: '',
      lastName: '',
      partyId: '',
      email: '',
      phone: {
        primary: blankPhone,
        mobile: null,
        secondary: blankPhone,
      },
    },
  },
};

export const userInfoSlice = createSlice({
  name: 'userInfo',
  initialState,
  reducers: {
    setUserInfo: (state, { payload }: { payload: Partial<UserInfoState['userInfo']> }) => {
      state.loading = false;
      const address = state.userInfo.address;
      const accountHolder = state.userInfo.accountHolder;
      state.userInfo = { ...state.userInfo, ...payload };
      state.userInfo.address = {
        ...address,
        ...payload.address,
      };
      state.userInfo.accountHolder = {
        ...accountHolder,
        ...payload.accountHolder,
      };
    },
    setAddress: (state, { payload }: { payload: AddressGroup }) => {
      state.userInfo.address = {
        ...state.userInfo.address,
        ...payload,
      };
    },
    setUpdateSuccess: (state) => {
      state.loading = false;
      state.wasUpdateSuccessful = true;
      state.error = null;
    },
    setUpdateError: (state, action) => {
      state.loading = false;
      state.wasUpdateSuccessful = false;
      state.error = action.payload;
    },
    resetUpdateStatus: (state) => {
      state.loading = false;
      state.wasUpdateSuccessful = false;
      state.error = null;
    },
    updateContactInfo: (state, _: { payload: UpdateAccountInfoInput }) => {
      state.loading = true;
    },
    updateContactInfoSMB: (state, _: { payload: UpdateAccountInfoSmbInput }) => {
      state.loading = true;
    },
    refetchUserInfo: (state) => {
      state.loading = true;
    },
  },
});

export const {
  setUserInfo,
  setUpdateSuccess,
  refetchUserInfo,
  updateContactInfo,
  updateContactInfoSMB,
  setAddress,
  resetUpdateStatus,
  setUpdateError,
} = userInfoSlice.actions;

export const selectUserInfo = (state: { userInfo: UserInfoState }): UserInfoState => {
  return { ...state.userInfo };
};
